import type { AccessLevel, AccessLevelProp, ButtonProps, ControlSize } from '@meterup/atto';
import type { Dispatch, ReactNode } from 'react';
import {
  Alert,
  Button,
  colors,
  ControlGroup,
  darkThemeSelector,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  Icon,
  Segments,
  styled,
  Text,
  TextInput,
  VStack,
} from '@meterup/atto';
import { type DurationLike, DateTime, Duration } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Timestamp } from '../../topics/time';
import type { DateTimeRangeSnapshot } from './DateTimeRangeStorageContext';
import type { IntervalUnitsProp } from './utils';
import { checkDefinedOrThrow } from '../../helpers/expectDefinedOrThrow';
import {
  formatDateTime,
  now,
  parseDateTime,
  stripDateForInput,
  stripDateTimeForInput,
  TimeFormat,
} from '../../topics/time';
import { FormatDateTime } from '../FormatDateTime';
import { useDateTimeRangeStateContextOrNull } from './DateTimeRangeStateContext';
import { useDateTimeRangeValue } from './DateTimeRangeStorageContext';
import {
  formatToISO,
  getIntervalDuration,
  getStep as defaultGetStep,
  IntervalUnits,
  intervalUnitsToSeconds,
  TimePeriod,
  timePeriodLabel,
} from './utils';

export interface UseDateTimeRangePickerProps {
  defaultValue?: TimePeriod;
  defaultStart?: Timestamp;
  defaultEnd?: Timestamp;
  intervalUnits?: IntervalUnits;
  allowCustom?: boolean | AccessLevel | null;
  fixAnchorToNow?: boolean;
  relativeTimeDirection?: 'future' | 'past';
  maxDuration?: DurationLike;
  stepSeconds?: number;
  getStep?: (value: number) => number;
}

export interface DateTimeRangeState extends UseDateTimeRangePickerProps {
  startDate: string; // ISO string start of range
  endDate: string; // ISO string end of range
  value: TimePeriod; // The selected time period
  /** @deprecated Use `intervalDuration` instead. */
  interval: number; // Total range duration in intervalUnits
  step: number;
  setTimePeriod: (value: TimePeriod) => void;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  refreshNow: () => void;
  reset: () => void;

  previousTimeRange: DateTimeRangeSnapshot | null;
  zoomToRange: (start: Date, end: Date) => void;
  resetZoom: () => void;

  intervalUnits: IntervalUnits;
  fixAnchorToNow: boolean;
  allowCustom?: boolean | AccessLevel | null;
  relativeTimeDirection: 'future' | 'past';

  /** End time if custom date range, or null if a preset range ending at now. */
  endTime: string | undefined;
  duration: Duration;
  getIntervalDuration: (units: IntervalUnits) => number;
  intervalDuration: Record<IntervalUnitsProp, number>;
}

export function useDateTimeRangePicker({
  defaultValue = TimePeriod.OneHour,
  defaultStart: defaultStartProp = now().minus(
    defaultValue === TimePeriod.Custom ? { days: 1 } : { seconds: defaultValue },
  ),
  defaultEnd: defaultEndProp = now(),
  intervalUnits = IntervalUnits.Seconds,
  allowCustom = null,
  fixAnchorToNow = true,
  relativeTimeDirection = 'past',
  maxDuration,
  stepSeconds,
  getStep = defaultGetStep,
}: UseDateTimeRangePickerProps = {}): DateTimeRangeState {
  const defaultStart = useMemo(
    () => stripDateTimeForInput(parseDateTime(defaultStartProp)!)!,
    [defaultStartProp],
  );
  const defaultEnd = useMemo(
    () => stripDateTimeForInput(parseDateTime(defaultEndProp)!)!,
    [defaultEndProp],
  );

  const [timePeriod, setTimePeriod] = useDateTimeRangeValue('timePeriod', defaultValue);

  const [startDate, setStartDate] = useDateTimeRangeValue('startDate', defaultStart);
  const [endDate, setEndDate] = useDateTimeRangeValue('endDate', defaultEnd);
  const [previousTimeRange, setPreviousTimeRange] = useDateTimeRangeValue(
    'previousTimeRange',
    null,
  );
  const [anchorNow, setAnchorNow] = useState(() => now().startOf('minute'));

  useEffect(() => {
    setAnchorNow(DateTime.now().startOf('minute'));
  }, [timePeriod]);

  const refreshNow = useCallback(() => {
    setAnchorNow(DateTime.now().startOf('minute'));
  }, []);

  const { calculatedStartDate, calculatedEndDate } = useMemo(() => {
    if (typeof timePeriod === 'number') {
      if (relativeTimeDirection === 'future') {
        return {
          calculatedStartDate: formatToISO(anchorNow)!,
          calculatedEndDate: formatToISO(anchorNow.plus({ seconds: timePeriod }))!,
        };
      }
      return {
        calculatedStartDate: formatToISO(anchorNow.minus({ seconds: timePeriod }))!,
        calculatedEndDate: formatToISO(anchorNow)!,
      };
    }

    let startISO = DateTime.fromISO(startDate);
    let endISO = DateTime.fromISO(endDate);
    if (fixAnchorToNow) {
      if (relativeTimeDirection === 'past') {
        endISO = DateTime.min(endISO, anchorNow);
      } else {
        startISO = DateTime.max(startISO, anchorNow);
      }
    }
    return {
      calculatedStartDate: formatToISO(startISO)!,
      calculatedEndDate: formatToISO(endISO)!,
    };
  }, [timePeriod, startDate, endDate, fixAnchorToNow, anchorNow, relativeTimeDirection]);

  const interval = useMemo(() => {
    if (timePeriod === TimePeriod.Custom) {
      if (!allowCustom) return getIntervalDuration(defaultValue, intervalUnits);

      const startDateCalc = DateTime.fromISO(calculatedStartDate);
      const endDateCalc = DateTime.fromISO(calculatedEndDate);
      const diff = endDateCalc.diff(startDateCalc, [intervalUnits]);
      return Math.round(diff.toObject()[intervalUnits] ?? 0);
    }

    return getIntervalDuration(timePeriod, intervalUnits);
  }, [
    calculatedStartDate,
    calculatedEndDate,
    timePeriod,
    allowCustom,
    defaultValue,
    intervalUnits,
  ]);

  const clampedInterval = useMemo(() => {
    if (maxDuration == null) {
      return interval;
    }

    return Math.min(
      getIntervalDuration(
        Duration.fromDurationLike(maxDuration).toMillis(),
        IntervalUnits.Milliseconds,
      ),
      interval,
    );
  }, [interval, maxDuration]);
  const step = stepSeconds ?? getStep(clampedInterval);

  const reset = useCallback(() => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setTimePeriod(defaultValue);
    setAnchorNow(DateTime.now().startOf('minute'));
  }, [
    setStartDate,
    setEndDate,
    setTimePeriod,
    setAnchorNow,
    defaultStart,
    defaultEnd,
    defaultValue,
  ]);

  const zoomToRange = useCallback(
    (start: Date, end: Date) => {
      setPreviousTimeRange(
        (prev) =>
          prev ?? {
            timePeriod,
            startDate: calculatedStartDate,
            endDate: calculatedEndDate,
          },
      );
      const nextStart = parseDateTime(start)?.toISO();
      const nextEnd = parseDateTime(end)?.toISO();
      if (!nextStart || !nextEnd) return;
      setStartDate(nextStart);
      setEndDate(nextEnd);
      setTimePeriod(TimePeriod.Custom);
    },
    [
      timePeriod,
      calculatedStartDate,
      calculatedEndDate,
      setPreviousTimeRange,
      setStartDate,
      setEndDate,
      setTimePeriod,
    ],
  );

  const resetZoom = useCallback(() => {
    if (previousTimeRange == null) return;
    setStartDate(previousTimeRange.startDate);
    setEndDate(previousTimeRange.endDate);
    setTimePeriod(previousTimeRange.timePeriod);
    setPreviousTimeRange(null);
  }, [previousTimeRange, setStartDate, setEndDate, setTimePeriod, setPreviousTimeRange]);

  useEffect(() => {
    if (timePeriod !== TimePeriod.Custom && previousTimeRange != null) {
      setPreviousTimeRange(null);
    }
  }, [timePeriod, previousTimeRange, setPreviousTimeRange]);

  return useMemo(() => {
    const seconds = clampedInterval * intervalUnitsToSeconds[intervalUnits];
    return {
      defaultValue,
      defaultStart,
      defaultEnd,
      intervalUnits,
      allowCustom,
      fixAnchorToNow,
      relativeTimeDirection,
      startDate: calculatedStartDate,
      endDate: calculatedEndDate,
      value: timePeriod,
      interval: clampedInterval,
      step,
      setStartDate,
      setEndDate,
      setTimePeriod,
      reset,
      refreshNow,
      previousTimeRange,
      zoomToRange,
      resetZoom,
      maxDuration,
      get endTime() {
        return timePeriod === TimePeriod.Custom ? calculatedEndDate : undefined;
      },
      get duration() {
        return Duration.fromDurationLike({ [intervalUnits]: clampedInterval });
      },
      getIntervalDuration(units) {
        return getIntervalDuration(seconds, units);
      },
      intervalDuration: {
        seconds,
        get milliseconds() {
          return getIntervalDuration(this.seconds, IntervalUnits.Milliseconds);
        },
        get minutes() {
          return getIntervalDuration(this.seconds, IntervalUnits.Minutes);
        },
        get hours() {
          return getIntervalDuration(this.seconds, IntervalUnits.Hours);
        },
      },
    };
  }, [
    clampedInterval,
    intervalUnits,
    defaultValue,
    defaultStart,
    defaultEnd,
    allowCustom,
    fixAnchorToNow,
    relativeTimeDirection,
    calculatedStartDate,
    calculatedEndDate,
    timePeriod,
    step,
    setStartDate,
    setEndDate,
    setTimePeriod,
    reset,
    refreshNow,
    previousTimeRange,
    zoomToRange,
    resetZoom,
    maxDuration,
  ]);
}

type TimePeriodOption = { accessLevel?: AccessLevelProp | null | undefined; value: TimePeriod };

type ChildrenOrOptions =
  | {
      children: ReactNode;
      options?: never;
    }
  | {
      children?: never;
      options: (TimePeriod | TimePeriodOption)[];
    };

type DateTimeRangePickerDropdownContentsProps = {
  state: DateTimeRangeState;
  /** @deprecated Use `dateTimeRangeState.maxDuration` instead */
  maxDuration?: DurationLike;
  onValueChange?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  setIsOpen?: Dispatch<boolean>;
  timeZone?: string;
  /** Only show the custom range inputs, without the relative time-period presets. */
  hidePresets?: boolean;
} & ChildrenOrOptions;

export type DateTimeRangePickerProps = Omit<DateTimeRangePickerDropdownContentsProps, 'state'> & {
  state?: DateTimeRangeState;
  size?: ControlSize;
  width?: ButtonProps['width'];
} & ChildrenOrOptions;

function normalizeOptions(options: (TimePeriod | TimePeriodOption)[]): TimePeriodOption[] {
  return options
    .map((option) => (typeof option === 'object' ? option : { value: option }))
    .filter((option) => option.accessLevel !== null);
}

const RangeLabelHyphen = styled(Text, {
  color: colors.gray200,

  [darkThemeSelector]: {
    color: colors.gray400,
  },
});
RangeLabelHyphen.displayName = 'RangeLabelHyphen';

type PickerSegment = 'presets' | 'custom';

export function DateTimeRangePickerDropdownContents({
  state: {
    setStartDate,
    setEndDate,
    setTimePeriod,
    startDate,
    endDate,
    interval,
    intervalUnits,
    value: dateTimeRangePickerValue,
    allowCustom,
    relativeTimeDirection,
    maxDuration,
  },
  children,
  maxDuration: deprecatedMaxDuration,
  options,
  onValueChange,
  // DASH-8517: For some reason the date/time inputs just completely ignore input if no `onKeyDown` property is being passed.
  // I don't know what caused this regression but this makes the inputs work again.
  onKeyDown = () => {},
  setIsOpen,
  timeZone,
  hidePresets = false,
}: DateTimeRangePickerDropdownContentsProps) {
  const normalizedOptions = useMemo(
    () => (options ? normalizeOptions(options) : undefined),
    [options],
  );

  const intervalSeconds = useMemo(
    () => intervalUnitsToSeconds[intervalUnits] * interval,
    [interval, intervalUnits],
  );

  useEffect(() => {
    if (
      dateTimeRangePickerValue === TimePeriod.Custom ||
      !normalizedOptions ||
      normalizedOptions.some((opt) => opt.value === intervalSeconds)
    )
      return;

    const descendingOptions = normalizedOptions
      .filter((o): o is typeof o & { value: number } => typeof o.value === 'number')
      .sort((a, b) => b.value - a.value);

    setTimePeriod(
      (descendingOptions.find((option) => option.value <= intervalSeconds) ?? normalizedOptions[0])
        .value,
    );
  }, [normalizedOptions, intervalSeconds, setTimePeriod, dateTimeRangePickerValue]);

  const [activeSegment, setActiveSegment] = useState<PickerSegment>(
    dateTimeRangePickerValue === TimePeriod.Custom ? 'custom' : 'presets',
  );
  const effectiveSegment = hidePresets ? 'custom' : activeSegment;

  const [startDateLocal, setStartDateLocal] = useState<string>(
    stripDateForInput(parseDateTime(startDate)!),
  );
  const [startTimeLocal, setStartTimeLocal] = useState<string>(
    formatDateTime(startDate, TimeFormat['24_HOUR_MINUTES']),
  );
  const [endDateLocal, setEndDateLocal] = useState<string>(
    stripDateForInput(parseDateTime(endDate)!),
  );
  const [endTimeLocal, setEndTimeLocal] = useState<string>(
    formatDateTime(endDate, TimeFormat['24_HOUR_MINUTES']),
  );

  // Keep local inputs in sync when external values change (e.g., presets or navigation)
  useEffect(() => {
    setStartDateLocal(stripDateForInput(parseDateTime(startDate)!));
    setStartTimeLocal(formatDateTime(startDate, TimeFormat['24_HOUR_MINUTES']));
  }, [startDate]);
  useEffect(() => {
    setEndDateLocal(stripDateForInput(parseDateTime(endDate)!));
    setEndTimeLocal(formatDateTime(endDate, TimeFormat['24_HOUR_MINUTES']));
  }, [endDate]);

  const nextStart = useMemo(
    () =>
      DateTime.fromISO(`${startDateLocal}T${startTimeLocal}`).setZone(timeZone, {
        keepLocalTime: true,
      }),
    [startDateLocal, startTimeLocal, timeZone],
  );

  const nextEnd = useMemo(
    () =>
      DateTime.fromISO(`${endDateLocal}T${endTimeLocal}`).setZone(timeZone, {
        keepLocalTime: true,
      }),
    [endDateLocal, endTimeLocal, timeZone],
  );

  const effectiveMaxDuration = maxDuration ?? deprecatedMaxDuration;

  const customErrorMessages = useMemo(() => {
    const errors = [];

    if (!nextStart.isValid) errors.push('Please provide a valid start date.');
    if (!nextEnd.isValid) errors.push('Please provide a valid end date.');
    if (nextStart.valueOf() > nextEnd.valueOf()) errors.push('End date must be after start date.');
    if (
      effectiveMaxDuration != null &&
      nextEnd.diff(nextStart).toMillis() >
        Duration.fromDurationLike(effectiveMaxDuration).toMillis()
    ) {
      errors.push(
        `Date range must be shorter than ${Duration.fromDurationLike(effectiveMaxDuration).toHuman()}.`,
      );
    }

    if (!errors.length) return null;

    return errors;
  }, [nextStart, nextEnd, effectiveMaxDuration]);

  const isDisabled = useMemo(() => {
    if (customErrorMessages?.length) return true;

    const nextStartISO = nextStart.toISO();
    const nextEndISO = nextEnd.toISO();

    return nextStartISO === startDate && nextEndISO === endDate;
  }, [customErrorMessages?.length, endDate, nextEnd, nextStart, startDate]);

  const handleApply = useCallback(() => {
    if (!nextStart.isValid || !nextEnd.isValid) return;
    if (isDisabled) return;

    // validity checked above
    const nextStartISO = nextStart.toISO()!;
    const nextEndISO = nextEnd.toISO()!;

    onValueChange?.();
    setStartDate(nextStartISO);
    setEndDate(nextEndISO);
    setTimePeriod(TimePeriod.Custom);
    setIsOpen?.(false);
  }, [
    nextStart,
    nextEnd,
    isDisabled,
    onValueChange,
    setStartDate,
    setEndDate,
    setTimePeriod,
    setIsOpen,
  ]);

  return (
    <>
      {allowCustom && !hidePresets && (
        <DropdownMenuGroup>
          <Segments
            proportions="equal"
            size="small"
            segments={[
              {
                label: 'Presets',
                selected: activeSegment === 'presets',
                onClick: () => setActiveSegment('presets'),
              },
              {
                label: 'Range',
                accessLevel: typeof allowCustom !== 'boolean' ? allowCustom : undefined,
                selected: activeSegment === 'custom',
                onClick: () => setActiveSegment('custom'),
              },
            ]}
          />
        </DropdownMenuGroup>
      )}
      {(!allowCustom || effectiveSegment === 'presets') && (
        <DropdownMenuRadioGroup
          label={`Filter by ${relativeTimeDirection}`}
          value={dateTimeRangePickerValue.toString()}
          onValueChange={(val: string) => {
            let value = TimePeriod.OneHour;
            if (val === TimePeriod.Custom) {
              value = val;
            }
            const num = Number.parseInt(val, 10);
            if (
              !Number.isNaN(num) &&
              Number.isInteger(num) &&
              Object.values(TimePeriod).includes(num)
            ) {
              value = num;
            }
            setTimePeriod(value);
          }}
        >
          {children ||
            normalizedOptions?.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value.toString()}
                accessLevel={option.accessLevel}
                onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLDivElement> | undefined}
              >
                {timePeriodLabel(option.value)}
              </DropdownMenuRadioItem>
            ))}
        </DropdownMenuRadioGroup>
      )}
      {allowCustom && effectiveSegment === 'custom' && (
        <>
          <DropdownMenuGroup>
            <VStack align="center" spacing={8}>
              <ControlGroup direction="row" size="small">
                <TextInput
                  type="date"
                  aria-label="Start date"
                  onChange={setStartDateLocal}
                  value={startDateLocal}
                  width="full"
                  onKeyDown={onKeyDown}
                />
                <TextInput
                  type="time"
                  aria-label="Start time"
                  onChange={setStartTimeLocal}
                  value={startTimeLocal}
                  width="full"
                  onKeyDown={onKeyDown}
                />
              </ControlGroup>
              <Icon icon="arrow-down" size={14} variant="neutral" />
              <ControlGroup direction="row" size="small">
                <TextInput
                  type="date"
                  aria-label="End date"
                  onChange={setEndDateLocal}
                  value={endDateLocal}
                  width="full"
                  onKeyDown={onKeyDown}
                />
                <TextInput
                  type="time"
                  aria-label="End time"
                  onChange={setEndTimeLocal}
                  value={endTimeLocal}
                  width="full"
                  onKeyDown={onKeyDown}
                />
              </ControlGroup>
              {!!customErrorMessages?.length && (
                <Alert
                  type="inline"
                  variant="negative"
                  copy={
                    <VStack spacing={4}>
                      {customErrorMessages.map((err, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Text key={`${index}:${err}`}>{err}</Text>
                      ))}
                    </VStack>
                  }
                />
              )}
            </VStack>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <Button size="small" variant="secondary" disabled={isDisabled} onClick={handleApply}>
              Apply
            </Button>
          </DropdownMenuGroup>
        </>
      )}
    </>
  );
}

export function DateTimeRangePicker({
  size,
  width,

  state: stateProp,

  ...props
}: DateTimeRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const stateContext = useDateTimeRangeStateContextOrNull();

  const state = checkDefinedOrThrow(
    stateProp ?? stateContext,
    new Error('DateTimeRangePicker must be provided a state or used in a DateTimeRangeProvider'),
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <DropdownMenuButton
        appearance="muted"
        arrangement="leading-icon"
        icon="clock"
        size={size}
        variant="secondary"
        width={width}
      >
        {state.value === 'CUSTOM' ? (
          <Text>
            <FormatDateTime value={state.startDate} format={TimeFormat.MEDIUM} />{' '}
            <RangeLabelHyphen>–</RangeLabelHyphen>{' '}
            <FormatDateTime value={state.endDate} format={TimeFormat.MEDIUM} />
          </Text>
        ) : (
          timePeriodLabel(state.value)
        )}
      </DropdownMenuButton>
      <DropdownMenuPopover align="start" side="bottom" sideOffset={6} width={240}>
        <DateTimeRangePickerDropdownContents state={state} {...props} />
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}
